import { DIALOGS } from './chat-screen-state';

// Mirrors the backend Chat::Presence preference enum.
export const PREFERENCES = Object.freeze({
  AUTOMATIC: 'automatic',
  OFFLINE: 'offline',
});

/**
 * Presence display + the Active Status picker. Merged into chatOverlay /
 * chatFullPage. Other users' live state lives in $store.chat.presence (one
 * source of truth, patched by PresenceChannel broadcasts); the current user's
 * own dot is driven by their chosen `presencePreference`, seeded from a
 * bootstrap attribute on the component root.
 */
export function presenceActions(currentUserId) {
  return {
    // Picker dialog selection — mirrors presencePreference while the dialog is open.
    pendingPreference: PREFERENCES.AUTOMATIC,
    presenceSaveError: false,
    _presenceSaveErrorTimer: null,

    // Whether another user shows an online dot. Single read point for the store
    // so templates bind to isOnline(id) rather than the store path directly.
    isOnline(userId) {
      return !!this.$store.chat.presence[userId];
    },

    // The self dot reflects the chosen preference, not live connectivity: a
    // connected user appears active under 'automatic' and intentionally gray
    // under 'offline'.
    selfShowsActive() {
      return this.presencePreference === PREFERENCES.AUTOMATIC;
    },

    // Live chat status label for another user, falling back to the value carried
    // on the payload before any broadcast has patched the store.
    chatStatusFor(userId, fallback = null) {
      return this.$store.chat.chatStatus[userId] ?? fallback;
    },

    // Seed the store from a conversations payload: `online` (DM partners live,
    // group members snapshot) and `chat_status` labels. Skips the current user.
    seedPresence(conversations = []) {
      conversations.forEach((conversation) => {
        const partner = conversation.other_participant;
        if (partner && partner.id !== currentUserId) {
          this.$store.chat.setPresence(partner.id, partner.online);
          this.$store.chat.setChatStatus(partner.id, partner.chat_status);
        }

        (conversation.members || []).forEach((member) => {
          if (member.user_id !== currentUserId) {
            this.$store.chat.setPresence(member.user_id, member.online);
            this.$store.chat.setChatStatus(member.user_id, member.chat_status);
          }
        });
      });
    },

    subscribePresence() {
      return this.$cable.subscribeToPresence({
        onPresence: (userId, online) => {
          this.$store.chat.setPresence(userId, online);
        },
        onStatus: (userId, label) => {
          this.$store.chat.setChatStatus(userId, label);
        },
        onPreference: (preference) => {
          this.presencePreference = preference;
        },
      });
    },

    openActiveStatus() {
      this.pendingPreference = this.presencePreference;
      this.dialog = DIALOGS.ACTIVE_STATUS;
    },

    closeActiveStatus() {
      this.dialog = null;
    },

    async saveActiveStatus() {
      const chosen = this.pendingPreference;
      const previous = this.presencePreference;

      if (chosen === previous) {
        this.closeActiveStatus();
        return;
      }

      // Optimistic: reflect the choice and close immediately; revert on error.
      this.presencePreference = chosen;
      this.closeActiveStatus();

      try {
        await this.$api({
          url: '/chat/presence_preference',
          method: 'PATCH',
          data: { preference: chosen },
        });
        this.trackChat('active status', 'toggle');
      } catch (err) {
        this.presencePreference = previous;
        this.showPresenceSaveError();
        console.error('Failed to update active status:', err);
      }
    },

    showPresenceSaveError() {
      this.presenceSaveError = true;
      clearTimeout(this._presenceSaveErrorTimer);
      this._presenceSaveErrorTimer = setTimeout(() => {
        this.presenceSaveError = false;
      }, 10000);
    },
  };
}